import React from 'react'
import { ColumnModel } from '@syncfusion/ej2-react-grids'
import { SwitchComponent } from '@syncfusion/ej2-react-buttons'
import {
  DropDownButtonComponent,
  ItemModel,
} from '@syncfusion/ej2-react-splitbuttons'
import { Button, DataGridComponent } from '@components/common'
import { ICampaignTriggerMessage } from '@dataTypes/marketing.type'

interface TimePointsComponentProps {
  data: ICampaignTriggerMessage[]
  disableDuplicate: boolean
  handleEdit: (data: ICampaignTriggerMessage) => void
  setData: (data: ICampaignTriggerMessage[]) => void
  onView: (data: ICampaignTriggerMessage) => void
}

const TimePointsComponent: React.FC<TimePointsComponentProps> = ({
  data,
  disableDuplicate,
  handleEdit,
  setData,
  onView,
}) => {
  // Handle switch toggle
  const handleToggle = (
    checked: boolean,
    rowData: ICampaignTriggerMessage,
  ): void => {
    const updatedData = data.map((item) =>
      item.campaign_trigger_message_id == rowData.campaign_trigger_message_id
        ? { ...item, status: checked ? 'Active' : 'Draft' }
        : item,
    )
    setData(updatedData)
  }

  const validateTimePoints = (props: ICampaignTriggerMessage): boolean => {
    const errors: { [key: string]: string } = {}
    if (!props.name || props.name.trim() === '') {
      errors.name = 'Trigger name is required'
    }
    if (!props.send_option || props.send_option === '') {
      errors.send_option = 'Select between "Before" or "After"'
    }
    if (!props.send_on || props.send_on === 0) {
      errors.send_on = 'Input number must be greater than 0'
    }
    if (!props.send_duration || props.send_duration === '') {
      errors.send_duration = 'Select applicable time option'
    }
    if (!props.text_content || props.text_content.trim() === '') {
      errors.text_content = 'Input campaign message'
    }
    return Object.keys(errors).length === 0
  }

  // Template for Switch component inside Grid column
  const switchTemplate = (props: ICampaignTriggerMessage): JSX.Element => {
    const isDisabled = !validateTimePoints(props)
    return (
      <span
        data-bs-toggle={isDisabled ? 'tooltip' : undefined}
        title={
          isDisabled
            ? 'This trigger is still in draft mode. Complete the missing data to activate.'
            : undefined
        }
      >
        <SwitchComponent
          key={props.campaign_trigger_message_id}
          checked={props.status === 'Active'}
          change={(e) => handleToggle(e.checked as boolean, props)}
          disabled={isDisabled}
        />
      </span>
    )
  }

  // Action dropdown items
  const actionItems: ItemModel[] = [
    { text: 'Edit' },
    { text: 'Duplicate', disabled: disableDuplicate },
    { text: 'Delete' },
  ]

  // Handle dropdown actions
  const handleAction = (args: any, rowData: ICampaignTriggerMessage): void => {
    const action = args.item.text

    if (action === 'Edit') {
      handleEdit(rowData)
    } else if (action === 'Duplicate') {
      if (disableDuplicate) return
      const newTrigger = {
        ...rowData,
        campaign_trigger_message_id: `new-${new Date().getTime()}`,
      }
      setData([...data, newTrigger])
    } else if (action === 'Delete') {
      const newData = data.filter(
        (t) =>
          t.campaign_trigger_message_id !== rowData.campaign_trigger_message_id,
      )
      setData(newData)
    }
  }
  const handleViewTriggerDetails = (props: ICampaignTriggerMessage): void => {
    onView(props)
  }

  // Action column template
  const actionTemplate = (props: ICampaignTriggerMessage): JSX.Element => {
    return (
      <div
        key={props.campaign_trigger_message_id}
        className="actions-container"
      >
        <Button
          data-bs-toggle="tooltip"
          title="View trigger details"
          className="button-primary"
          onClick={() => handleViewTriggerDetails(props)}
        >
          <i className="mgc_eye_2_fill" />
        </Button>
        <DropDownButtonComponent
          items={actionItems}
          select={(args) => handleAction(args, props)}
          iconCss="ti ti-dots-vertical"
          cssClass="e-caret-hide" // hides the default arrow caret
        />
      </div>
    )
  }

  const columns: ColumnModel[] = [
    {
      headerText: 'Status',
      template: (props: ICampaignTriggerMessage) => switchTemplate(props),
      width: 0.13,
      textAlign: 'Center',
    },
    {
      field: 'name',
      headerText: 'Trigger name',
      width: 0.36,
    },
    {
      field: 'send_on',
      headerTemplate: () => (
        <span className="e-headertext">
          Trigger point
          <i
            data-bs-toggle="tooltip"
            className="mgc_information_fill"
            title={`Trigger points set when the message will be sent relative to the campaign date.
Example:
  • 4 days before → message is sent 4 days before the campaign start date.`}
          />
        </span>
      ),
      template: (props: ICampaignTriggerMessage) => (
        <span key={props.campaign_trigger_message_id}>
          {props.send_on && props.send_duration && props.send_option
            ? `${props.send_on} ${props.send_duration} ${props.send_option}
          `.toLowerCase()
            : '-'}
        </span>
      ),
      width: 0.36,
      type: 'number',
    },
    {
      headerText: 'Actions',
      template: (props: ICampaignTriggerMessage) => actionTemplate(props),
      width: 0.15,
      textAlign: 'Center',
    },
  ]

  return (
    <DataGridComponent
      key="time-points-data-grid"
      data={data}
      columns={columns}
    />
  )
}

export default TimePointsComponent
